import { Inject } from '@odinjs/odin';
import { BasicFile, FieldAPI, FileUploaderAPI, FormAPI } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnClick, OnMount, onRemove } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { AbortableSubmitEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, MyChartDocumentAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import DocumentContext from '../../utils/document-context';
import { FileRuleUtils } from '../../utils/file-rules-utils';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';

interface FormNewDocumentProps {
  files: BasicFile[];
}

interface DocumentSentProps extends Record<string, Value> {
  filePath: string;
  size: number;
  title: string;
}

interface SendDocumentDTO {
  id: number;
}

@Controller({ fragment: FragmentNames.MY_CHART_DOCUMENT })
export default class MyChartSendDocumentController extends BaseController {

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject(MyChartDocumentAliases.FIELD_UPLOADER)
    fieldFileUploaderAPI!: FieldAPI;

  @Inject(MyChartDocumentAliases.FORM_DOCUMENT)
    formDocumentAPI!: FormAPI;

  @OnMount(MyChartDocumentAliases.FILE_DOCUMENT_UPLOADER)
  async onMountFileUploader(event: MountEvent): Promise<void> {
    const fileUploaderApi = event.target as FileUploaderAPI;
    FileRuleUtils.validateFileUploaderRules(fileUploaderApi);
  }

  @OnMount(MyChartDocumentAliases.FIELD_UPLOADER)
  async handleFieldFileUploaderLoading(event: MountEvent): Promise<void> {
    const fieldUploadAPI = event.target as FieldAPI;
    await fieldUploadAPI.validate();
  }

  @onRemove(MyChartDocumentAliases.FILE_DOCUMENT_UPLOADER)
  async handleFileUploaderChange(): Promise<void> {
    await this.fieldFileUploaderAPI.validate();
  }

  @OnBeforeSubmit({ target: MyChartDocumentAliases.FORM_DOCUMENT })
  async handleFormDocumentSubmit(event: AbortableSubmitEvent): Promise<void> {
    if (!(await this.fieldFileUploaderAPI.validate())) {
      event.abort();
      return;
    }

    this.formDocumentAPI.setLoading(true);

    const { files } = event.record as FormNewDocumentProps;
    const body = files.map<DocumentSentProps>(file => ({
      filePath: file.filepath,
      size: file.size,
      title: file.description || ''
    })) as unknown as Record<string, Value>;

    HttpService.post(
      APPLICATION_ENDPOINTS.POST_MC_SEND_DOCUMENT,
      { patientId: UserContext.patientId },
      { body }
    ).then(response => {
      const sendDocumentList = response.body as SendDocumentDTO[];
      const documentsCode = [];
      for(const r of sendDocumentList ){
        documentsCode.push(r.id);
      }
      DocumentContext.documentIds = documentsCode;

      this.submittableFragmentContext.submit(new SubmitEvent());
      showToastNotificationSuccess('patient.message.files-uploaded-successfully');
    }).catch(() => {
      showToastNotificationError('newaccount.error.internal.server.error.try.again');
    }).finally(() => {
      this.formDocumentAPI.setLoading(false);
    });
  }

  @OnClick({ target: MyChartDocumentAliases.BUTTON_CANCEL })
  handleBtnCancel(): void {
    this.submittableFragmentContext.cancel(new SubmitEvent());
  }
}